import { createSlice } from '@repo/store';

/**
 * Thrown when the user voluntarily cancels an auth flow (closes the auth
 * sheet, dismisses the OAuth popup, etc.). This is NOT a bug — it's a
 * normal user choice — and is filtered out of PostHog error tracking
 * via `filterPostHogEvent` in `lib/analytics.ts`.
 *
 * Callers that want to handle cancellation explicitly can use
 * `if (err instanceof AuthCancelledError)` to distinguish it from a
 * real auth failure.
 */
export class AuthCancelledError extends Error {
  constructor(message: string = 'Auth flow cancelled') {
    super(message);
    this.name = 'AuthCancelledError';
    // Preserve prototype chain for `instanceof` to work after TS down-leveling
    Object.setPrototypeOf(this, AuthCancelledError.prototype);
  }
}

export type AuthStep =
  | 'email-input'
  | 'login-otp'
  | 'signup-otp'
  | 'signup-details'
  | 'verify-phone';

type FormData = {
  email: string;
  code: string;
  firstName: string;
  lastName: string;
  phone: string;
  marketingConsent: boolean;
  transactionalConsent: boolean;
  isSmsMarketingEnabled: boolean;
  isSmsTransactionalEnabled: boolean;
  autoSendPhoneOtp?: boolean; // Flag to auto-send OTP when verify-phone step loads
};

type OtpState = {
  resendAt: number | null; // epoch ms when resend becomes available
};

export type AuthUIState = {
  isOpen: boolean;
  locked: boolean; // when true, sheet cannot be dismissed
  step: AuthStep;
  mode: 'login' | 'signup' | null;
  config: { title?: string; description?: string };
  formData: FormData;
  otp: OtpState;
  profileCompletionPromptMutedUntil: number | null; // epoch ms; when set, suppresses ProfileCompletionPrompt until this time
  // With `exactOptionalPropertyTypes`, we can't reliably "clear" optional function fields.
  // Use explicit `undefined` instead (non-optional) so we can always reset them.
  pendingResolve: (() => void) | undefined;
  pendingReject: ((err?: unknown) => void) | undefined;
};

const initialForm: FormData = {
  email: '',
  code: '',
  firstName: '',
  lastName: '',
  phone: '',
  marketingConsent: false,
  transactionalConsent: false,
  isSmsMarketingEnabled: false,
  isSmsTransactionalEnabled: false,
};

export const authUISlice = createSlice({
  name: 'authUI',
  initialState: {
    isOpen: false,
    locked: false,
    step: 'email-input' as AuthStep,
    mode: null as AuthUIState['mode'],
    config: { title: 'Log in or sign up' },
    formData: initialForm,
    otp: { resendAt: null },
    profileCompletionPromptMutedUntil: null,
    pendingResolve: undefined,
    pendingReject: undefined,
  } as AuthUIState,
  // Persist only non-control UI fields
  // Do NOT persist isOpen/locked/step/otp timers/pending resolvers to avoid restoring an in-flight flow
  persist: { keys: ['formData', 'profileCompletionPromptMutedUntil'] },
  actions: ({ setSlice }) => ({
    /**
     * Begin the auth flow and return a promise that resolves/rejects when the flow completes.
     * This centralises the pending resolver in the store so any component can settle it.
     */
    begin: (config?: { title?: string; description?: string; locked?: boolean }) => {
      // Open/reset UI state
      setSlice({
        isOpen: true,
        locked: Boolean(config?.locked),
        step: 'email-input',
        mode: null,
        config: { title: config?.title ?? '', description: config?.description ?? '' },
        formData: { ...initialForm },
        otp: { resendAt: null },
      });

      // Create a promise and stash its resolvers
      let resolve!: () => void;
      let reject!: (err?: unknown) => void;
      const p = new Promise<void>((res, rej) => {
        resolve = res;
        reject = rej;
      });
      setSlice({ pendingResolve: resolve, pendingReject: reject });
      return p;
    },
    /** Open the auth sheet to update profile (non-blocking, no pending promise). */
    openProfileUpdate: () => {
      setSlice(prev => ({
        isOpen: true,
        locked: false,
        step: 'signup-details',
        mode: prev.mode ?? null,
        config: { title: 'Update profile' },
      }));
    },
    setLocked: (locked: boolean) => setSlice({ locked }),
    close: () =>
      setSlice(prev => {
        // If a flow is awaiting this modal (begin() was called), closing must settle the promise.
        // Otherwise callers like `withAuth()` can hang forever (e.g. review submit loader stuck).
        try {
          prev.pendingReject?.(new AuthCancelledError());
        } catch {
          // best-effort
        }
        return {
          isOpen: false,
          locked: false,
          step: 'email-input',
          mode: null,
          config: {},
          formData: { ...initialForm },
          otp: { resendAt: null },
          pendingResolve: undefined,
          pendingReject: undefined,
        };
      }),
    setStep: (step: AuthStep) => setSlice({ step }),
    setMode: (mode: 'login' | 'signup' | null) => setSlice({ mode }),
    updateConfig: (cfg: Partial<{ title: string; description: string }>) =>
      setSlice(prev => ({ config: { ...prev.config, ...cfg } })),
    updateForm: (data: Partial<FormData>) =>
      setSlice(prev => ({ formData: { ...prev.formData, ...data } })),
    setOtpResendAt: (epochMs: number | null) => setSlice({ otp: { resendAt: epochMs } }),
    setProfileCompletionPromptMutedUntil: (epochMs: number | null) =>
      setSlice({ profileCompletionPromptMutedUntil: epochMs }),
    /** Resolve the pending auth flow promise (if any) and reset UI. */
    resolvePending: () =>
      setSlice(prev => {
        try {
          prev.pendingResolve?.();
        } finally {
          // close and clear pending
          const next: Partial<AuthUIState> = {
            isOpen: false,
            locked: false,
            step: 'email-input',
            mode: null,
            config: {},
            formData: { ...initialForm },
            otp: { resendAt: null },
            pendingResolve: undefined,
            pendingReject: undefined,
          };
          return next;
        }
      }),
    /** Reject the pending auth flow promise (if any) and reset UI. */
    rejectPending: (err?: unknown) =>
      setSlice(prev => {
        try {
          prev.pendingReject?.(err);
        } finally {
          const next: Partial<AuthUIState> = {
            isOpen: false,
            locked: false,
            step: 'email-input',
            mode: null,
            config: {},
            formData: { ...initialForm },
            otp: { resendAt: null },
            pendingResolve: undefined,
            pendingReject: undefined,
          };
          return next;
        }
      }),
    reset: () =>
      setSlice({
        isOpen: false,
        locked: false,
        step: 'email-input',
        mode: null,
        config: { title: 'Log in or sign up' },
        formData: { ...initialForm },
        otp: { resendAt: null },
        profileCompletionPromptMutedUntil: null,
      }),
  }),
});

export type AuthUISlice = AuthUIState & {
  begin: (config?: { title?: string; description?: string; locked?: boolean }) => Promise<void>;
  openProfileUpdate: () => void;
  setLocked: (locked: boolean) => void;
  close: () => void;
  setStep: (step: AuthStep) => void;
  setMode: (mode: 'login' | 'signup' | null) => void;
  updateConfig: (cfg: Partial<{ title: string; description: string }>) => void;
  updateForm: (data: Partial<FormData>) => void;
  setOtpResendAt: (epochMs: number | null) => void;
  setProfileCompletionPromptMutedUntil: (epochMs: number | null) => void;
  resolvePending: () => void;
  rejectPending: (err?: unknown) => void;
  reset: () => void;
};
